import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, Download, Loader2, ArrowRight, Ticket, Sun, Moon, XCircle, Clock } from 'lucide-react';
import SenArtisteLogo from '../components/SenArtisteLogo';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [artistSlug, setArtistSlug] = useState(null);
  const [paymentState, setPaymentState] = useState('loading'); // 'loading', 'success', 'pending', 'failed', 'error'
  
  const [pollingCount, setPollingCount] = useState(0);
  const pollingRef = React.useRef(0);
  const MAX_POLLING_ATTEMPTS = 15; // Maximum 15 tentatives (60 secondes)
  
  const sessionId = searchParams.get('session_id');
  const paymentId = searchParams.get('payment_id');
  const provider = searchParams.get('provider'); // 'stripe', 'paydunya', 'dkr', 'dkr-wave', 'dkr-orange'
  
  // Fonction pour formater le prix selon la devise
  const formatPrice = (amount, currency) => {
    if (currency === 'FCFA' || currency === 'XOF') {
      return `${amount?.toLocaleString('fr-FR') || 0} F CFA`;
    }
    // Pour EUR, le montant Stripe est en centimes
    return `${(amount / 100).toFixed(2)} €`;
  };
  
  useEffect(() => {
    if (sessionId) {
      // Paiement Stripe
      checkStripePaymentStatus();
    } else if (paymentId && provider === 'paydunya') {
      // Paiement PayDunya (legacy)
      checkPayDunyaPaymentStatus();
    } else if (paymentId && (provider?.startsWith('dkr') || provider === 'dkr')) {
      // Paiement DKR Proxy (Wave ou Orange Money)
      checkDkrPaymentStatus();
    } else if (paymentId) {
      // Payment ID sans provider - essayer de récupérer les billets
      fetchTickets();
    } else {
      setLoading(false);
    }
  }, [sessionId, paymentId, provider]);
  
  const checkStripePaymentStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/stripe/checkout/status/${sessionId}`);
      setPaymentStatus(response.data);
      
      if (response.data.metadata?.artist_slug) {
        setArtistSlug(response.data.metadata.artist_slug);
      }
      
      if (response.data.payment_status === 'paid') {
        setPaymentState('success');
        toast.success('Paiement confirmé !');
        if (paymentId) {
          await fetchTickets();
        }
      } else {
        setPaymentState('pending');
      }
    } catch (error) {
      console.error('Error checking Stripe payment status:', error);
      setPaymentState('error');
      toast.error('Erreur lors de la vérification du paiement');
    } finally {
      setLoading(false);
    }
  };
  
  const checkPayDunyaPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/paydunya/status/${paymentId}`);
      setPaymentStatus({
        ...response.data,
        currency: 'FCFA',
        amount_display: response.data.amount
      });
      
      if (response.data.artist_slug) {
        setArtistSlug(response.data.artist_slug);
      }
      
      if (response.data.status === 'completed') {
        setPaymentState('success');
        toast.success('Paiement confirmé !');
        await fetchTickets();
        setLoading(false);
      } else if (response.data.status === 'pending') {
        setPaymentState('pending');
        // Polling limité pour éviter boucle infinie
        setPollingCount(prev => prev + 1);
        if (pollingCount < MAX_POLLING_ATTEMPTS) {
          setTimeout(() => checkPayDunyaPaymentStatus(), 3000);
        } else {
          // Après 30 secondes, considérer comme en attente
          toast.info('Le paiement est en cours de traitement. Vos billets seront disponibles sous peu.');
          setLoading(false);
        }
        return;
      } else if (response.data.status === 'failed' || response.data.status === 'cancelled') {
        setPaymentState('failed');
        setLoading(false);
      } else {
        setPaymentState('pending');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking PayDunya payment status:', error);
      setPaymentState('error');
      toast.error('Erreur lors de la vérification du paiement');
      setLoading(false);
    }
  };
  
  const checkDkrPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/dkr/status/${paymentId}`);
      setPaymentStatus({
        ...response.data,
        currency: response.data.currency || 'FCFA',
        amount_display: response.data.amount
      });
      
      if (response.data.artist_slug) {
        setArtistSlug(response.data.artist_slug);
      }
      
      if (response.data.status === 'completed') {
        setPaymentState('success');
        toast.success('Paiement confirmé !');
        await fetchTickets();
        setLoading(false);
      } else if (response.data.status === 'pending') {
        setPaymentState('pending');
        // Polling avec useRef pour éviter les problèmes de closure
        pollingRef.current += 1;
        setPollingCount(pollingRef.current);
        if (pollingRef.current < MAX_POLLING_ATTEMPTS) {
          setTimeout(() => checkDkrPaymentStatus(), 4000); // 4 secondes pour DKR
        } else {
          toast.info('Le paiement est en cours de traitement. Vos billets seront disponibles sous peu.');
          setLoading(false);
        }
        return;
      } else if (response.data.status === 'failed' || response.data.status === 'cancelled') {
        setPaymentState('failed');
        setLoading(false);
      } else {
        setPaymentState('pending');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking DKR payment status:', error);
      setPaymentState('error');
      toast.error('Erreur lors de la vérification du paiement');
      setLoading(false);
    }
  };
  
  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API_URL}/tickets/by-payment/${paymentId}`);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };
  
  const downloadTicketPDF = async (ticket) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(212, 175, 55); // Gold color matching Sen Artiste
    doc.rect(0, 0, 210, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('SEN ARTISTE', 105, 20, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Votre billet électronique', 105, 32, { align: 'center' });
    
    // Ticket info - Event title
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(ticket.eventTitle || 'Événement', 105, 60, { align: 'center' });
    
    // Ticket number prominently displayed
    doc.setFillColor(212, 175, 55);
    doc.rect(20, 68, 170, 20, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`N° ${ticket.ticketNumber || ticket.id.slice(0, 8).toUpperCase()}`, 105, 82, { align: 'center' });
    
    // Catégorie et Prix du billet
    const ticketTypeName = ticket.ticketTypeName || 'Standard';
    const ticketPrice = ticket.paidPrice || ticket.ticketPrice || 0;
    const currency = ticket.currency || 'FCFA';
    const priceDisplay = currency === 'EUR' 
      ? `${(ticketPrice / 100).toFixed(2)} €` 
      : `${ticketPrice.toLocaleString('fr-FR')} F CFA`;
    
    doc.setFillColor(245, 245, 245);
    doc.rect(20, 92, 170, 14, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 60, 60);
    doc.text(ticketTypeName, 25, 101);
    doc.setTextColor(212, 175, 55);
    doc.text(priceDisplay, 185, 101, { align: 'right' });
    
    // Infos événement et acheteur
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const yStart = 118;
    doc.text(`Date: ${ticket.eventDate ? new Date(ticket.eventDate).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}`, 20, yStart);
    doc.text(`Lieu: ${ticket.eventLocation || 'N/A'}`, 20, yStart + 10);
    doc.text(`Acheteur: ${ticket.buyerName}`, 20, yStart + 22);
    doc.text(`Email: ${ticket.buyerEmail}`, 20, yStart + 32);
    
    // QR Code - use ticketNumber for QR data
    const qrData = ticket.ticketNumber || ticket.id;
    const qrDataUrl = await QRCode.toDataURL(qrData, { width: 150 });
    doc.addImage(qrDataUrl, 'PNG', 70, yStart + 45, 70, 70);
    
    // Code sous QR
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Code: ${ticket.ticketNumber || ticket.id}`, 105, yStart + 120, { align: 'center' });
    
    // Footer
    doc.setFillColor(30, 30, 30);
    doc.rect(0, 275, 210, 22, 'F');
    doc.setFontSize(8);
    doc.setTextColor(180, 180, 180);
    doc.text('Présentez ce QR code à l\'entrée de l\'événement', 105, 284, { align: 'center' });
    doc.text('Ce billet est personnel et non transférable - senartiste.com', 105, 292, { align: 'center' });
    
    doc.save(`billet-${ticket.ticketNumber || ticket.id.slice(0, 8)}.pdf`);
    toast.success('Billet téléchargé !');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center hero-gradient">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg">Vérification du paiement...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen py-8 px-4 hero-gradient">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="fixed top-4 right-4 z-50"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </Button>
      
      <div className="max-w-xl mx-auto pt-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          {/* État SUCCESS */}
          {paymentState === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="font-syne text-3xl font-bold mb-2">Paiement réussi !</h1>
              <p className="text-muted-foreground">
                Merci pour votre achat. Vos billets sont prêts.
              </p>
            </>
          )}
          
          {/* État PENDING */}
          {paymentState === 'pending' && (
            <>
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="font-syne text-3xl font-bold mb-2">Paiement en attente</h1>
              <p className="text-muted-foreground">
                Votre paiement est en cours de traitement. Vos billets seront disponibles sous peu.
              </p>
            </>
          )}
          
          {/* État FAILED */}
          {paymentState === 'failed' && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="font-syne text-3xl font-bold mb-2">Paiement échoué</h1>
              <p className="text-muted-foreground">
                Le paiement n'a pas pu être confirmé. Veuillez réessayer.
              </p>
            </>
          )}
          
          {/* État ERROR */}
          {paymentState === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="font-syne text-3xl font-bold mb-2">Erreur de vérification</h1>
              <p className="text-muted-foreground">
                Impossible de vérifier le statut du paiement. Si vous avez été débité, contactez le support.
              </p>
            </>
          )}
        </motion.div>
        
        {paymentStatus && paymentState === 'success' && (
          <Card className="glass-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Détails de la transaction
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Montant</span>
                <span className="font-medium">
                  {paymentStatus.currency === 'FCFA' 
                    ? formatPrice(paymentStatus.amount_display || paymentStatus.amount, 'FCFA')
                    : formatPrice(paymentStatus.amount_total, 'EUR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Statut</span>
                <span className="text-green-500 font-medium">Payé</span>
              </div>
            </CardContent>
          </Card>
        )}
        
        {tickets.length > 0 && (
          <Card className="glass-card mb-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Vos billets ({tickets.length})</span>
                {tickets.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => tickets.forEach(t => downloadTicketPDF(t))}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Tout télécharger
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {tickets.map((ticket, index) => (
                <div key={ticket.id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
                  <div className="flex-1">
                    <p className="font-bold text-lg">Billet #{index + 1}</p>
                    <p className="text-sm font-mono bg-primary/10 px-2 py-1 rounded inline-block mt-1">
                      {ticket.ticketNumber || ticket.id.slice(0, 8).toUpperCase()}
                    </p>
                    {ticket.ticketTypeName && ticket.ticketTypeName !== 'Standard' && (
                      <p className="text-sm font-medium text-primary mt-1">{ticket.ticketTypeName}</p>
                    )}
                    {ticket.eventTitle && (
                      <p className="text-sm text-muted-foreground mt-1">{ticket.eventTitle}</p>
                    )}
                  </div>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => downloadTicketPDF(ticket)}
                    className="ml-4"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger PDF
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate(artistSlug ? `/${artistSlug}` : '/')}>
            Retour à l'accueil
          </Button>
          <Button className="btn-primary" onClick={() => navigate(-2)}>
            <ArrowRight className="w-4 h-4 mr-2" />
            Voir l'événement
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
